/**
 * PostgREST typed accessors for classes / class_sessions / bookings (plan 07
 * Task 10). RLS scopes rows to the caller's tenant; member tokens see only their
 * own bookings (Pattern B). These are read paths — mutations go through the
 * bookings / class-sessions Edge Functions.
 */
import type { BookingStatus } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

export interface ClassSessionRange {
  from: string; // ISO timestamptz inclusive
  to: string; // ISO timestamptz inclusive
  locationId?: string;
  onlyPt?: boolean;
}

/** List class templates for the tenant (RLS-enforced). */
export function listClasses(client: GymSupabaseClient) {
  return client.from('classes').select('*').eq('is_active', true).order('name');
}

/** Calendar range of sessions; optionally filter to a location or PT-only. */
export function listClassSessions(client: GymSupabaseClient, range: ClassSessionRange) {
  let q = client
    .from('class_sessions')
    .select('*')
    .gte('starts_at', range.from)
    .lte('starts_at', range.to)
    .order('starts_at');
  if (range.locationId) q = q.eq('location_id', range.locationId);
  if (range.onlyPt !== undefined) q = q.eq('is_pt', range.onlyPt);
  return q;
}

/** PT-only sessions for the instructor scheduling view (CS-06). */
export function listPtSessions(client: GymSupabaseClient, range: ClassSessionRange) {
  return listClassSessions(client, { ...range, onlyPt: true });
}

/** A member's own bookings (Pattern B RLS); pass statuses to filter. */
export function listMemberBookings(
  client: GymSupabaseClient,
  memberId: string,
  statuses?: BookingStatus[],
) {
  let q = client
    .from('bookings')
    .select('*')
    .eq('member_id', memberId)
    .order('booked_at', { ascending: false });
  if (statuses && statuses.length > 0) q = q.in('status', statuses);
  return q;
}

/** A member's waitlist entries. */
export function listMemberWaitlist(client: GymSupabaseClient, memberId: string) {
  return client
    .from('waitlist')
    .select('*')
    .eq('member_id', memberId)
    .eq('status', 'waiting')
    .order('joined_at');
}
